import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Crown, Save, Eye, Plus, Trash2, Edit2, X, Users, MessageSquare } from 'lucide-react';

const FoundersCornerTab = () => {
  const [content, setContent] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingContent, setEditingContent] = useState(false);
  
  const [formData, setFormData] = useState({
    welcome_title: '',
    welcome_message: '',
    founder_message: '',
    founder_name: '',
    exclusive_updates: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentRes, membersRes] = await Promise.all([
        api.get('/admin/founders-corner/content'),
        api.get('/admin/founders-corner/members')
      ]);
      
      setContent(contentRes.data.content);
      setMembers(membersRes.data.members || []);
      
      if (contentRes.data.content) {
        setFormData({
          welcome_title: contentRes.data.content.welcome_title || '',
          welcome_message: contentRes.data.content.welcome_message || '',
          founder_message: contentRes.data.content.founder_message || '',
          founder_name: contentRes.data.content.founder_name || '',
          exclusive_updates: contentRes.data.content.exclusive_updates || ''
        });
      }
    } catch (error) {
      console.error('Error fetching founders corner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      await api.put('/admin/founders-corner/content', formData);
      toast.success('Founders Corner content saved!');
      setEditingContent(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove founding member badge from this user?')) return;
    
    try {
      await api.delete(`/admin/founders-corner/members/${userId}`);
      toast.success('Member removed from Founders Corner');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading Founders Corner data...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Crown className="w-8 h-8 text-[#C5A52A]" />
          <div>
            <h2 className="text-2xl font-bold text-white">Founders Corner</h2>
            <p className="text-gray-500 text-sm">Manage exclusive content for founding members</p>
          </div>
        </div>
        <a
          href="/founders-corner"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          <Eye className="w-4 h-4" />
          Preview Page
        </a>
      </div>

      {/* Content Editor */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg mb-6">
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h3 className="font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#C5A52A]" />
            Page Content
          </h3>
          {!editingContent ? (
            <button
              onClick={() => setEditingContent(true)}
              className="flex items-center gap-2 px-3 py-1 bg-[#C5A52A] hover:bg-[#d4b43a] text-black font-bold rounded text-sm transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleSaveContent}
                disabled={saving}
                className="flex items-center gap-2 px-3 py-1 bg-green-600 hover:bg-green-700 text-white font-bold rounded text-sm transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEditingContent(false)}
                className="flex items-center gap-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="p-6 space-y-4">
          {editingContent ? (
            <>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Welcome Title</label>
                <input
                  type="text"
                  value={formData.welcome_title}
                  onChange={(e) => setFormData({ ...formData, welcome_title: e.target.value })}
                  placeholder="Welcome to the Founders Corner"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Welcome Message</label>
                <textarea
                  value={formData.welcome_message}
                  onChange={(e) => setFormData({ ...formData, welcome_message: e.target.value })}
                  rows={4}
                  placeholder="Thank you for being one of our founding members..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Founder Message</label>
                  <textarea
                    value={formData.founder_message}
                    onChange={(e) => setFormData({ ...formData, founder_message: e.target.value })}
                    rows={4}
                    placeholder="A personal message from the founder..."
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Founder Name/Signature</label>
                  <input
                    type="text"
                    value={formData.founder_name}
                    onChange={(e) => setFormData({ ...formData, founder_name: e.target.value })}
                    placeholder="- The CYQR Team"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Exclusive Updates <span className="text-gray-600">(supports markdown-style formatting)</span>
                </label>
                <textarea
                  value={formData.exclusive_updates}
                  onChange={(e) => setFormData({ ...formData, exclusive_updates: e.target.value })}
                  rows={6}
                  placeholder="Share exclusive news, upcoming features, behind-the-scenes updates..."
                  className="w-full bg-gray-800 border border-gray-700 rounded px-4 py-2 text-white resize-none font-mono text-sm"
                />
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">Welcome Title</p>
                <p className="text-white">{content?.welcome_title || <span className="text-gray-600 italic">Not set</span>}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">Welcome Message</p>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{content?.welcome_message || <span className="text-gray-600 italic">Not set</span>}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase mb-1">Founder Message</p>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{content?.founder_message || <span className="text-gray-600 italic">Not set</span>}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Members List */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg">
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#C5A52A]" />
            Founding Members ({members.length})
          </h3>
        </div>

        <div className="p-4">
          {members.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No founding members yet. Award the "founding-member" badge to users to grant access.
            </p>
          ) : (
            <div className="space-y-2">
              {members.map((member) => (
                <div 
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded"
                >
                  <div className="flex items-center gap-3">
                    {member.profile_image_url ? (
                      <img 
                        src={member.profile_image_url} 
                        alt="" 
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#C5A52A]/20 flex items-center justify-center">
                        <Crown className="w-5 h-5 text-[#C5A52A]" />
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">{member.name}</p>
                      <p className="text-gray-500 text-xs">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-500">
                      Joined: {new Date(member.earned_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleRemoveMember(member.user_id)}
                      className="p-2 text-red-400 hover:bg-red-900/30 rounded transition-colors"
                      title="Remove founding member badge"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-800 bg-gray-800/30">
          <p className="text-xs text-gray-500">
            To add a founding member, go to <strong>Badges</strong> tab and award the "founding-member" badge to the user.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FoundersCornerTab;
